import { z } from 'zod';

export const clientSchema = z.object({
  name: z.string()
    .min(1, 'El nombre es requerido')
    .min(2, 'El nombre debe tener al menos 2 caracteres')
    .max(100, 'El nombre no puede exceder 100 caracteres')
    .trim(),
  
  email: z.string()
    .email('Formato de email inválido')
    .optional()
    .or(z.literal('')),
  
  phone: z.string()
    .optional()
    .refine((val) => {
      if (!val) return true;
      return val.length >= 10;
    }, 'El teléfono debe tener al menos 10 caracteres'),
  
  country: z.string()
    .optional(),
  
  address: z.string()
    .optional(),
  
  city: z.string()
    .optional(),
  
  postal_code: z.string()
    .optional(),
  
  tax_id: z.string()
    .optional(),
  
  is_company: z.boolean()
    .default(false),
  
  company_size: z.string()
    .optional(),
  
  industry: z.string()
    .optional(),
  
  website: z.string()
    .url('Formato de URL inválido')
    .optional()
    .or(z.literal('')),
  
  notes: z.string()
    .optional(),
  
  status: z.enum(['active', 'inactive', 'pending'])
    .default('active'),
  
  contact_person: z.string()
    .optional(),
  
  contact_email: z.string()
    .email('Formato de email inválido')
    .optional()
    .or(z.literal('')),
  
  contact_phone: z.string()
    .optional(),
  
  // Campos para archivos/documentos
  avatar: z.string()
    .optional(),
  
  documents: z.array(z.string())
    .optional()
    .default([]),
  
  // Metadatos
  tags: z.array(z.string())
    .optional()
    .default([]),
  
  priority: z.enum(['low', 'medium', 'high'])
    .default('medium'),
  
  created_date: z.string()
    .optional(),
  
  last_contact: z.string()
    .optional()
});

export const clientFormSchema = clientSchema.omit({
  created_date: true,
  documents: true
});

// Esquemas para diferentes operaciones
export const createClientSchema = clientFormSchema;
export const updateClientSchema = clientFormSchema.partial().extend({
  name: z.string().min(1, 'El nombre es requerido') // Name is still required for updates
});

// Esquema para filtros de búsqueda
export const clientFiltersSchema = z.object({
  name: z.string().optional(),
  email: z.string().optional(),
  status: z.enum(['active', 'inactive', 'pending', 'all']).optional(),
  is_company: z.union([z.boolean(), z.string()]).optional(),
  country: z.string().optional(),
  priority: z.enum(['low', 'medium', 'high', 'all']).optional(),
  industry: z.string().optional()
});

// Tipos derivados para TypeScript (si se usa)
export const CLIENT_STATUSES = [
  { value: 'active', label: 'Activo' },
  { value: 'inactive', label: 'Inactivo' },
  { value: 'pending', label: 'Pendiente' }
];

export const CLIENT_PRIORITIES = [
  { value: 'low', label: 'Baja' },
  { value: 'medium', label: 'Media' },
  { value: 'high', label: 'Alta' }
];

export const CLIENT_TYPES = [
  { value: false, label: 'Persona Natural' },
  { value: true, label: 'Empresa' }
];

export const COMPANY_SIZES = [
  { value: 'startup', label: 'Startup (1-10)' },
  { value: 'small', label: 'Pequeña (11-50)' },
  { value: 'medium', label: 'Mediana (51-200)' },
  { value: 'large', label: 'Grande (201-1000)' },
  { value: 'enterprise', label: 'Corporación (1000+)' }
];

export const INDUSTRIES = [
  { value: 'technology', label: 'Tecnología' },
  { value: 'healthcare', label: 'Salud' },
  { value: 'finance', label: 'Finanzas' },
  { value: 'education', label: 'Educación' },
  { value: 'retail', label: 'Retail' },
  { value: 'manufacturing', label: 'Manufactura' },
  { value: 'construction', label: 'Construcción' },
  { value: 'agriculture', label: 'Agricultura' },
  { value: 'energy', label: 'Energía' },
  { value: 'entertainment', label: 'Entretenimiento' },
  { value: 'transportation', label: 'Transporte' },
  { value: 'other', label: 'Otro' }
];

// Validación de un cliente individual
export const validateClient = (data) => {
  try {
    return {
      success: true,
      data: clientFormSchema.parse(data),
      errors: {}
    };
  } catch (error) {
    const errors = {};
    error.errors.forEach((err) => {
      errors[err.path[0]] = err.message;
    });
    return {
      success: false,
      data: null,
      errors
    };
  }
};

// Función helper para limpiar datos del cliente
export const sanitizeClientData = (data) => {
  // Remover campos vacíos o undefined
  const cleaned = Object.fromEntries(
    Object.entries(data).filter(([_, value]) => {
      if (value === null || value === undefined) return false;
      if (typeof value === 'string' && value.trim() === '') return false;
      return true;
    })
  );

  // Convertir strings a boolean para is_company si es necesario
  if ('is_company' in cleaned) {
    cleaned.is_company = Boolean(cleaned.is_company);
  }

  return cleaned;
}; 